import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {POST_SERVICE_URL} from 'urls';
import {getQueryString} from 'utils/queryString';
import request from 'utils/request';
import {
  CONNECT_FOLDER,
  connectFolderFailed,
  connectFolderSuccess,
  COPY_MOVE_POST,
  copyMoveSuccess,
  CREATE_POST,
  createPostFailed,
  createPostSuccess,
  FETCH_FOLDER,
  FETCH_FOLDERS,
  FETCH_POST,
  FETCH_POST_GALLERY,
  FETCH_POSTS,
  FETCH_WEEKLY,
  fetchFolderFailed,
  fetchFoldersFailed,
  fetchFoldersSuccess,
  fetchFolderSuccess,
  fetchPostByIdFailed,
  fetchPostByIdSuccess,
  fetchPostGalleryFailed,
  fetchPostGallerySuccess,
  fetchPostsFailed,
  fetchPostsSuccess,
  fetchWeeklyFailed,
  fetchWeeklySuccess,
  SEND_TO,
  sendToGalleryFailed,
  sendToGallerySuccess,
  UPDATE_POST,
  updatePostFailed,
  updatePostSuccess,
} from 'actions/postActions';
import {
  makeSelectUserRoles,
  makeSelectUserToken,
} from 'reducers/selectors/userSelectors';

const getMoveUrl = (action) => {
  let url = '';
  if (action.isCopy) {
    url = action.pageFolderId
      ? `post/${action.id}/move?id=${action.pageFolderId}&isCopy=true`
      : `post/${action.id}/move?isCopy=true`;
  } else {
    url = `post/${action.id}/move?id=${action.pageFolderId}`;
  }
  return url;
};

function* fetchPosts(action) {
  const params = getQueryString(action.filters);
  const requestUrl = `${POST_SERVICE_URL}${action.pageFolderId}/filterByPage?${params}`;
  const authToken = yield select(makeSelectUserToken());
  const requestOptions = {};
  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);

    yield put(
      fetchPostsSuccess(
        response.posts,
        response.statuses,
        response.total,
        response.pageNumberBack
      )
    );
  } catch (err) {
    yield put(fetchPostsFailed(err));
  }
}

function* fetchWeekly() {
  const requestUrl = `${POST_SERVICE_URL}/filterByWeekly`;
  const authToken = yield select(makeSelectUserToken());
  const requestOptions = {};
  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(fetchWeeklySuccess(response.posts, response.statuses));
  } catch (err) {
    yield put(fetchWeeklyFailed(err));
  }
}

function* updatePost(action) {
  const authToken = yield select(makeSelectUserToken());
  let requestUrl = action.single
    ? `${POST_SERVICE_URL}${action.id}/update`
    : `${POST_SERVICE_URL}${action.id}`;

  if (action.revisionId !== undefined)
    requestUrl = `${POST_SERVICE_URL}${action.id}/revision/${action.revisionId}`;
  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(action.details),
  };

  try {
    yield call(request, requestUrl, authToken, requestOptions);
    // yield put(updatePostSuccess(post, action.id));
    yield put(updatePostSuccess(action, action.id));
  } catch (err) {
    yield put(updatePostFailed(err));
  }
}

function* createPost(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `post`;
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(action.details),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);

    yield put(createPostSuccess(response.post));
  } catch (err) {
    yield put(createPostFailed(err));
  }
}

// function* deletePost(action) {
//   const authToken = yield select(makeSelectUserToken());
//   const requestUrl = `post`;
//   const requestOptions = {
//     method: 'DELETE',
//     body: JSON.stringify(action.details),
//   };
//
//   try {
//     const response = yield call(request, requestUrl, authToken, requestOptions);
//     yield delete (createPostSuccess(response.post));
//   } catch (err) {
//     yield delete(createPostFailed(err));
//   }
// }

function* fetchPostById(action) {
  let requestUrl = `${POST_SERVICE_URL}${action.id}`;
  if (action.revisionId !== undefined)
    requestUrl = `${POST_SERVICE_URL}${action.id}/revision/${action.revisionId}`;
  const authToken = yield select(makeSelectUserToken());
  const roles = yield select(makeSelectUserRoles());
  if (roles.find((r) => ['partner', 'judge'].includes(r.title)))
    requestUrl = `${POST_SERVICE_URL}public/id/${action.id}`;
  const requestOptions = {};
  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    if (response && response._id) yield put(fetchPostByIdSuccess(response));
    else yield put(fetchPostByIdFailed('error on fetch post'));
  } catch (err) {
    yield put(fetchPostByIdFailed(err));
  }
}

function* fetchPostGallery(action) {
  const requestUrl = `document/post/gallery/${action.id}?pageSize=1000`;
  const authToken = yield select(makeSelectUserToken());

  const requestOptions = {};
  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(fetchPostGallerySuccess(response.folders, response.extraFolders));
  } catch (err) {
    yield put(fetchPostGalleryFailed(err));
  }
}

function* copyMove(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = getMoveUrl(action);
  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(action.details),
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(copyMoveSuccess(action.isCopy, response.post, action.id));
  } catch (err) {
    yield put(connectFolderFailed(err));
  }
}

/**
 * Abandon API handle it directly when updatePost()
 * @param action
 * @returns {Generator<SimpleEffect<"PUT", PutEffectDescriptor<{err: *, type: string}>>|SimpleEffect<"PUT", PutEffectDescriptor<{type: string, posts: *}>>|SimpleEffect<"SELECT", SelectEffectDescriptor>|SimpleEffect<"CALL", CallEffectDescriptor<(function(string, *, Object=): Promise<Response>)|* extends ((...args: any[]) => SagaIterator<infer RT>) ? RT : ((function(string, *, Object=): Promise<Response>)|* extends ((...args: any[]) => Promise<infer RT>) ? RT : ((function(string, *, Object=): Promise<Response>)|* extends ((...args: any[]) => infer RT) ? RT : never))>>, void, *>}
 */
function* connectFolder(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `post/${action.postId}/${action.companyId}/connect`;
  const requestOptions = {
    method: 'PUT',
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(connectFolderSuccess(response.post));
  } catch (err) {
    yield put(connectFolderFailed(err));
  }
}

function* sendToGallery(action) {
  const authToken = yield select(makeSelectUserToken());
  const requestUrl = `company/${action.postId}/${action.folderId}/connect`;

  const requestOptions = {
    method: 'PUT',
  };

  try {
    const response = yield call(request, requestUrl, authToken, requestOptions);
    yield put(sendToGallerySuccess(response.post));
  } catch (err) {
    yield put(sendToGalleryFailed(err));
  }
}

function* fetchFolders(action) {
  const authToken = yield select(makeSelectUserToken());
  const filters = {...action.filters};
  if (
    !filters.folderId ||
    filters.folderId === undefined ||
    filters.folderId === 'undefined'
  ) {
    delete filters.folderId;
    filters.folderType = 'FOLDER';
    filters.parentFolderId = null;
  }
  const params = getQueryString(filters);
  const URL = `document/folders?${params}`;

  try {
    const response = yield call(request, URL, authToken, {});
    yield put(
      fetchFoldersSuccess(
        response.folders,
        response.total,
        response.folder,
        response.path
      )
    );
  } catch (err) {
    yield put(fetchFoldersFailed(err));
  }
}

function* fetchFolder(action) {
  const authToken = yield select(makeSelectUserToken());
  const URL = `document/folder/${action.folderId}?test=ruo`;
  try {
    const response = yield call(request, URL, authToken, {});
    yield put(fetchFolderSuccess(response.folder));
  } catch (err) {
    yield put(fetchFolderFailed(err));
  }
}

export default function* postSaga() {
  yield all([
    takeLatest(FETCH_POSTS, fetchPosts),
    takeLatest(CREATE_POST, createPost),
    takeEvery(UPDATE_POST, updatePost),
    takeLatest(FETCH_POST, fetchPostById),
    takeLatest(FETCH_POST_GALLERY, fetchPostGallery),
    takeLatest(COPY_MOVE_POST, copyMove),
    takeLatest(FETCH_WEEKLY, fetchWeekly),
    takeLatest(CONNECT_FOLDER, connectFolder),
    takeLatest(SEND_TO, sendToGallery),
    takeLatest(FETCH_FOLDERS, fetchFolders),
    takeLatest(FETCH_FOLDER, fetchFolder),
  ]);
}
